import dayjs, { Dayjs } from 'dayjs'
import {
  defaultDiagnosisInfo,
  END_DATE_PLACEHOLDER,
  START_DATE_PLACEHOLDER
} from './SocAssessment/constants/constants'
import { Diagnosis } from './components/types'

type RawDiagnosesData = {
  description: string
  icd10_code: string
  id: number
}[]

export const formatDiagnoses = (
  rawDiagnoses: RawDiagnosesData
): { value: string; label: string }[] => {
  return rawDiagnoses.map((el) => ({
    value: `${el.icd10_code} - ${el.description}`,
    label: `${el.icd10_code} - ${el.description}`
  }))
}

/**
 * Convert diagnoses flat answers to form data
 * @param primaryDiagnosisValues - The primary diagnosis values
 * @param otherDiagnosisValues - The other diagnosis values
 * @returns The form data
 */
export const convertDiagnosesFlatAnswersToFormData = (
  primaryDiagnosisValues: string[] | undefined,
  otherDiagnosisValues: string[] | undefined
): {
  primaryDiagnosis: Diagnosis | undefined
  otherDiagnoses: Array<Diagnosis>
} => {
  const backgroundPrimaryDiagnosis: Diagnosis | undefined =
    primaryDiagnosisValues && primaryDiagnosisValues.length >= 4
      ? {
          diagnosis: primaryDiagnosisValues[0],
          rating: primaryDiagnosisValues[1],
          date: [
            primaryDiagnosisValues[2] && primaryDiagnosisValues[2] !== START_DATE_PLACEHOLDER
              ? dayjs(primaryDiagnosisValues[2]).utc()
              : undefined,
            primaryDiagnosisValues[3] && primaryDiagnosisValues[3] !== END_DATE_PLACEHOLDER
              ? dayjs(primaryDiagnosisValues[3]).utc()
              : undefined
          ]
        }
      : undefined

  const backgroundOtherDiagnosis: Array<Diagnosis> = []
  if (otherDiagnosisValues && otherDiagnosisValues.length) {
    for (let i = 0; i < otherDiagnosisValues.length; i += 4) {
      if (i + 3 < otherDiagnosisValues.length && otherDiagnosisValues[i]) {
        backgroundOtherDiagnosis.push({
          diagnosis: otherDiagnosisValues[i],
          rating: otherDiagnosisValues[i + 1],
          date: [
            otherDiagnosisValues[i + 2] && otherDiagnosisValues[i + 2] !== START_DATE_PLACEHOLDER
              ? dayjs(otherDiagnosisValues[i + 2]).utc()
              : undefined,
            otherDiagnosisValues[i + 3] && otherDiagnosisValues[i + 3] !== END_DATE_PLACEHOLDER
              ? dayjs(otherDiagnosisValues[i + 3]).utc()
              : undefined
          ]
        })
      }
    }
  }
  return {
    primaryDiagnosis: backgroundPrimaryDiagnosis,
    otherDiagnoses: backgroundOtherDiagnosis
  }
}

/**
 * Convert primary diagnosis form data to flat answers
 * @param primaryDiagnosis - The primary diagnosis
 * @returns The flat answers as a string array
 */
export const convertPrimaryDiagnosisToFlatAnswers = (
  primaryDiagnosis: Diagnosis | undefined
): string[] => {
  return primaryDiagnosis
    ? [
        primaryDiagnosis.diagnosis,
        primaryDiagnosis.rating,
        primaryDiagnosis.date?.[0]?.utc()?.toISOString() ?? defaultDiagnosisInfo[2],
        primaryDiagnosis.date?.[1]?.utc()?.toISOString() ?? defaultDiagnosisInfo[3]
      ]
    : []
}

/**
 * Convert other diagnoses form data to flat answers
 * @param otherDiagnoses - The other diagnoses
 * @returns The flat answers as a string array
 */
export const convertOtherDiagnosesToFlatAnswers = (otherDiagnoses: Diagnosis[]): string[] => {
  const otherDiagnosesValues: string[] = []
  if (otherDiagnoses?.length) {
    for (const diagnosis of otherDiagnoses) {
      otherDiagnosesValues.push(
        ...[
          diagnosis.diagnosis,
          diagnosis.rating,
          diagnosis.date?.[0]?.utc()?.toISOString() ?? defaultDiagnosisInfo[2],
          diagnosis.date?.[1]?.utc()?.toISOString() ?? defaultDiagnosisInfo[3]
        ]
      )
    }
  }
  return otherDiagnosesValues
}

export const convertAppointmentTimeFormDataToFlatAnswers = (
  fieldValue: [Dayjs | undefined, Dayjs | undefined]
): {
  appointmentTimeValues: [string | undefined, string | undefined]
} => {
  const [start, end] = Array.isArray(fieldValue) ? fieldValue : [undefined, undefined]
  const startUtc = start ? dayjs(start).utc().toISOString() : undefined
  const endUtc = end ? dayjs(end).utc().toISOString() : undefined
  return {
    appointmentTimeValues: [startUtc, endUtc]
  }
}

export const convertTimeToUTCString = (time: Dayjs | string): string | undefined => {
  return time ? dayjs(time).utc().toISOString() : undefined
}
